import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import './ManageProducts.css';

function ProductRowActions({ 
  product, 
  onProductAction, 
  isReadOnly, 
  userRole, 
  userId,
  isDropdownOpen = false,
  onDropdownToggle
}) {
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showActiveModal, setShowActiveModal] = useState(false);
  const [showUnlistModal, setShowUnlistModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [draftReason, setDraftReason] = useState('');
  const [activeReason, setActiveReason] = useState('');
  const [unlistReason, setUnlistReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: -9999, right: 12 });
  
  const showDropdown = isDropdownOpen;
  const setShowDropdown = (value) => {
    if (onDropdownToggle) {
      onDropdownToggle(value);
    }
  };
  
  const handleAction = (action) => {
    setShowDropdown(false);
    
    if (action === 'draft') {
      setShowDraftModal(true);
    } else if (action === 'active') {
      setShowActiveModal(true);
    } else if (action === 'unlisted') {
      setShowUnlistModal(true);
    } else if (action === 'delete') {
      setShowDeleteModal(true);
    } else {
      onProductAction(action, product.id);
    }
  };

  const handleDraftConfirm = () => {
    if (!draftReason.trim()) {
      alert('Please provide a reason for moving to draft.');
      return;
    }
    console.log('[DRAFT] Sending draft action with reason:', draftReason);
    onProductAction('draft', product.id, { reason: draftReason });
    setShowDraftModal(false);
    setDraftReason('');
  };

  const handleUnlistConfirm = () => {
    onProductAction('unlisted', product.id, { reason: unlistReason });
    setShowUnlistModal(false);
    setUnlistReason('');
  };

  const handleActiveConfirm = () => {
    onProductAction('active', product.id, { reason: activeReason });
    setShowActiveModal(false);
    setActiveReason('');
  };
  
  const handleDeleteConfirm = () => {
    if (!deleteReason.trim()) {
      alert('Please provide a reason for deletion.');
      return;
    }
    onProductAction('delete', product.id, { reason: deleteReason });
    setShowDeleteModal(false);
    setDeleteReason('');
  };
  
  const getAvailableActions = () => {
    const actions = [];
    
    // Determine if user can delete this product
    const canDelete = !isReadOnly && (userRole === 'admin' || userRole === 'vendor');
    
    // Determine if user can manage status (Admin only)
    const canManageStatus = !isReadOnly && (userRole === 'admin' || userRole === 'vendor');
    
    // Draft action - only if not already draft
    if (canManageStatus && product.terminal_status !== 'draft') {
      actions.push({ key: 'draft', label: 'Move to Draft' });
    }

    if (canManageStatus && product.terminal_status !== 'active') {
      actions.push({ key: 'active', label: 'Move to Active' });
    }

    // Unlisted = stays live on Shopify but comes off the Online Store sales channel.
    if (canManageStatus && product.terminal_status !== 'unlisted') {
      actions.push({ key: 'unlisted', label: 'Move to Unlisted' });
    }

    // Delete action - destructive, always last
    if (canDelete) {
      actions.push({ key: 'delete', label: 'Delete', destructive: true });
    }
    
    return actions;
  };
  
  const availableActions = getAvailableActions();

  useLayoutEffect(() => {
    if (!showDropdown) {
      return undefined;
    }

    const updatePosition = () => {
      if (!buttonRef.current) {
        return;
      }

      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuHeight = menuRef.current?.offsetHeight || 0;
      const offset = 8;

      const spaceAbove = rect.top;
      const spaceBelow = viewportHeight - rect.bottom;

      let top;
      if (menuHeight && spaceAbove >= menuHeight + offset) {
        top = Math.max(offset, rect.top - menuHeight - offset);
      } else if (menuHeight && spaceBelow >= menuHeight + offset) {
        top = rect.bottom + offset;
      } else if (menuHeight) {
        if (spaceAbove >= spaceBelow) {
          top = Math.max(offset, rect.top - menuHeight - offset);
        } else {
          top = Math.max(offset, viewportHeight - menuHeight - offset);
        }
      } else {
        top = rect.bottom + offset;
      }

      const right = Math.max(offset, viewportWidth - rect.right);
      setMenuPosition({ top, right });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showDropdown]);
  
  if (availableActions.length === 0) {
    return <span className="noActions">No actions available</span>;
  }

  const renderRowActionModal = (modal) => {
    if (typeof document === 'undefined') {
      return null;
    }

    return createPortal(modal, document.body);
  };
  
  return (
    <div className="productRowActions">
      {/* Dropdown for actions (Move to Draft, Delete) */}
      {availableActions.length > 0 && (
        <div className="actionDropdown">
          <button
            ref={buttonRef}
            onClick={() => setShowDropdown(!showDropdown)}
            className="dropdownToggle"
            disabled={isReadOnly}
            title="More actions"
          >
            ⋯
          </button>
          
          {showDropdown && (
            <>
              <div 
                className="dropdownOverlay"
                onClick={() => setShowDropdown(false)}
              />
              <div
                ref={menuRef}
                className="dropdownMenu"
                style={{ top: menuPosition.top, right: menuPosition.right }}
              >
                {availableActions.map((action) => (
                  <button
                    key={action.key}
                    onClick={() => handleAction(action.key)}
                    className={`dropdownAction ${action.destructive ? 'destructive' : ''}`}
                    disabled={isReadOnly}
                  >
                    <span className="actionLabel">{action.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Draft Confirmation Modal */}
      {showDraftModal && renderRowActionModal(
        <div className="modalOverlay">
          <div className="modalContent">
            <div className="modalHeader">
              <h3>Move to Draft</h3>
              <button 
                className="modalClose"
                onClick={() => setShowDraftModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modalBody">
              <p>Are you sure you want to move this product to draft status?</p>
              <div className="formGroup">
                <label htmlFor="draftReason">Reason (required):</label>
                <textarea
                  id="draftReason"
                  value={draftReason}
                  onChange={(e) => setDraftReason(e.target.value)}
                  placeholder="Enter reason for moving to draft..."
                  rows="3"
                  required
                />
              </div>
            </div>
            <div className="modalFooter">
              <button 
                className="modalButton secondary"
                onClick={() => setShowDraftModal(false)}
              >
                Cancel
              </button>
              <button 
                className="modalButton primary"
                onClick={handleDraftConfirm}
                disabled={!draftReason.trim()}
              >
                Move to Draft
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Active Confirmation Modal */}
      {showActiveModal && renderRowActionModal(
        <div className="modalOverlay">
          <div className="modalContent">
            <div className="modalHeader">
              <h3>Move to Active</h3>
              <button
                className="modalClose"
                onClick={() => setShowActiveModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modalBody">
              <p>
                This will set the product to <strong>Active</strong> and push it to Shopify
                straight away, so it goes live without a separate approval step.
              </p>
              <div className="formGroup">
                <label htmlFor="activeReason">Reason (optional):</label>
                <textarea
                  id="activeReason"
                  value={activeReason}
                  onChange={(e) => setActiveReason(e.target.value)}
                  placeholder="Enter reason for moving to active..."
                  rows="3"
                />
              </div>
            </div>
            <div className="modalFooter">
              <button
                className="modalButton secondary"
                onClick={() => setShowActiveModal(false)}
              >
                Cancel
              </button>
              <button
                className="modalButton primary"
                onClick={handleActiveConfirm}
              >
                Move to Active
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unlist Confirmation Modal */}
      {showUnlistModal && renderRowActionModal(
        <div className="modalOverlay">
          <div className="modalContent">
            <div className="modalHeader">
              <h3>Move to Unlisted</h3>
              <button
                className="modalClose"
                onClick={() => setShowUnlistModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modalBody">
              <p>
                This removes the product from the <strong>Online Store</strong> so customers
                can no longer see or buy it. It stays Active in Shopify and keeps its data,
                and moves to the <strong>Unlisted Products</strong> tab here.
              </p>
              <div className="formGroup">
                <label htmlFor="unlistReason">Reason (optional):</label>
                <textarea
                  id="unlistReason"
                  value={unlistReason}
                  onChange={(e) => setUnlistReason(e.target.value)}
                  placeholder="Enter reason for unlisting..."
                  rows="3"
                />
              </div>
            </div>
            <div className="modalFooter">
              <button
                className="modalButton secondary"
                onClick={() => setShowUnlistModal(false)}
              >
                Cancel
              </button>
              <button
                className="modalButton primary"
                onClick={handleUnlistConfirm}
              >
                Move to Unlisted
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && renderRowActionModal(
        <div className="modalOverlay">
          <div className="modalContent">
            <div className="modalHeader">
              <h3>Delete Product</h3>
              <button 
                className="modalClose"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modalBody">
              <p className="warningText">
                ⚠️ This will perform a soft delete. The product will be archived but can be restored.
              </p>
              <div className="formGroup">
                <label htmlFor="deleteReason">Reason for deletion (required):</label>
                <textarea
                  id="deleteReason"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Enter reason for deletion..."
                  rows="3"
                  required
                />
              </div>
            </div>
            <div className="modalFooter">
              <button 
                className="modalButton secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                className="modalButton danger"
                onClick={handleDeleteConfirm}
                disabled={!deleteReason.trim()}
              >
                Delete Product
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductRowActions;
